// Which sidebar rows light up for a URL; the expanded list and the collapsed rail share these, so both modes agree.

/**
 * Whether a nav item should render as active for a given URL.
 *
 * <p>Default match is "URL equals item.path OR is one of its subpaths
 * (startsWith path + '/')". Two optional flags let an item override:
 *
 * <ul>
 *   <li>{@code exact: true} — only the equality branch matches. Use
 *       it when an item's own `path` is a strict prefix of a sibling's
 *       owned subtree, so that being on the sibling's subtree does NOT
 *       light up this item. Today: Game Registration's path
 *       {@code /game-mgmt/games} is a prefix of Game Workspace's
 *       per-game URLs {@code /game-mgmt/games/:gameId/...}; without
 *       `exact` they'd both light up.</li>
 *   <li>{@code matchPathPrefixes: string[]} — widens the match to also
 *       activate when the URL starts with any of these prefixes. Used
 *       by Game Workspace to claim the per-game URLs even though its
 *       own landing lives at {@code /game-mgmt/workspace}. Each prefix
 *       should include the trailing {@code '/'} so the bare parent
 *       path (e.g. {@code /games}) doesn't get over-matched.</li>
 *   <li>{@code matchPathPattern: string} — a regex (tested, not
 *       anchored at the end) that also activates the item when the URL
 *       matches. Needed when the distinguishing segment sits in the
 *       MIDDLE of the URL, which a prefix can't capture — e.g. the
 *       Game Workspace tab children claim
 *       {@code /game-mgmt/games/:gameClientId/<tab>} where the variable
 *       game id precedes the tab.</li>
 * </ul>
 */
export const matchesActivePath = (item, currentPath) => {
    if (!item?.path) return false;
    if (currentPath === item.path) return true;
    if (item.matchPathPrefixes?.some(p => currentPath.startsWith(p))) return true;
    if (item.matchPathPattern && new RegExp(item.matchPathPattern).test(currentPath)) return true;
    if (item.exact) return false;
    return currentPath.startsWith(item.path + '/');
};

// Whether any of these items, or anything under them, matches the URL.
export const isSubtreeActive = (items, currentPath) => (items || []).some(sub =>
    matchesActivePath(sub, currentPath) || isSubtreeActive(sub.subItems, currentPath));

// Whether the row is the page itself. A row with children only on its own path (Games on /game), unless a child shares it.
export const isNodeActive = (node, currentPath) => {
    if (!node?.path) return false;
    if (!node.subItems?.length) return matchesActivePath(node, currentPath);
    return currentPath === node.path && !node.subItems.some(sub => sub.path === node.path);
};

// The collapsed rail's row for a section; it keeps `exact`, or a game's Homepage lights up on every game page.
export const collapsedSectionItem = (section) => ({
    path: section.path, label: section.label, icon: section.icon, subItems: section.subItems,
    isLocked: section.isLocked,
    exact: section.exact, matchPathPrefixes: section.matchPathPrefixes, matchPathPattern: section.matchPathPattern,
});
